import styled from 'styled-components';
import * as bp from 'view/styles/breakpoints';
import * as fonts from 'view/styles/fonts';
import * as mixins from 'view/styles/mixins';
import * as transitions from 'view/styles/transitions';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { FakeButton } from '@atlassian/help-center-common-component/fake-button';

// eslint-disable-next-line rulesdir/no-styled-export
export const IconWrap = styled.div`
    /* Important to not let this shrink in the flex container. Else it won't be visible in Chrome/Safari. */
    flex-shrink: 0;

    ${bp.fromSmall.css`
        /* Apply margin from mobile, when on a small viewport we want a bit of padding between the right edge and the icon. */
        margin-left: ${token('space.100', '8px')};
    `};

    svg {
        transition: background-color ${transitions.speedMs.fast}ms ${transitions.timingFunctions.easeInOut},
            ${transitions.speedMs.fast}ms ${transitions.timingFunctions.easeInOut};
        border-radius: 50%;
    }
`;

/**
 * Watch out! Changing anything that will affect height (fontsize, padding, etc) in here will
 * break the clamping logic inside announcement-banner.tsx (look for SINGLE_LINE_TITLE_HEIGHT).
 * If you change anything here make sure it still works!
 */
// eslint-disable-next-line rulesdir/no-styled-export
export const Root = styled(FakeButton)`
    ${mixins.borderRadius};
    ${mixins.subtleThemeColorBackground};
    ${fonts.regular};
    display: block;
    width: 100%;
    text-align: left;
    transition: color ${transitions.speedMs.fast}ms ${transitions.timingFunctions.easeInOut};
    color: ${token('color.text', colors.N800)};
    /* Important to set box sizing so padding doesn't change the roots width. */
    box-sizing: border-box;
    /* Important to hide overflow because since we make the chevron bust out of the container, we don't want a scrollbar to appear. */
    overflow: hidden;

    &.focus-visible,
    &:hover ${IconWrap} {
        svg {
            background-color: ${token('color.background.input', colors.N800)};
            color: ${token('color.text.inverse', colors.N0)};
            opacity: 0.7;
        }
    }
`;

interface TitleProps {
    isExpanded?: boolean;
}

/**
 * Watch out! Changing anything that will affect height (fontsize, padding, etc) in here will
 * break the clamping logic inside announcement-banner.tsx (look for SINGLE_LINE_TITLE_HEIGHT).
 * If you change anything here make sure it still works!
 */
// eslint-disable-next-line rulesdir/no-styled-export
export const Title = styled.div<TitleProps>`
    ${fonts.medium};
    ${(props) => mixins.applyIf(!props.isExpanded, mixins.clamp)};
    margin-bottom: ${token('space.100', '8px')};
    font-weight: 600;
`;

interface MessageProps {
    isExpanded: boolean;
}

/**
 * Watch out! Changing anything that will affect height (fontsize, padding, etc) in here will
 * break the clamping logic inside announcement-banner.tsx (look for SINGLE_LINE_TITLE_HEIGHT).
 * If you change anything here make sure it still works!
 */
// eslint-disable-next-line rulesdir/no-styled-export
export const Message = styled.div<MessageProps>`
    /* We use multiline clamp because the message can be multi line, having unique P tags for each paragraph */
    /* If we use a single line clamp it won't work unfortunately. */
    ${(props) => mixins.applyIf(!props.isExpanded, mixins.multiLineClamp(1))}
`;

/**
 * Watch out! Changing anything that will affect height (fontsize, padding, etc) in here will
 * break the clamping logic inside announcement-banner.tsx (look for SINGLE_LINE_TITLE_HEIGHT).
 * If you change anything here make sure it still works!
 */
// eslint-disable-next-line rulesdir/no-styled-export
export const Container = styled.div`
    display: flex;
    justify-content: space-between;
    padding-top: ${token('space.300', '24px')};
    padding-bottom: ${token('space.300', '24px')};
`;

/**
 * Watch out! Changing anything that will affect height (fontsize, padding, etc) in here will
 * break the clamping logic inside announcement-banner.tsx (look for SINGLE_LINE_TITLE_HEIGHT).
 * If you change anything here make sure it still works!
 */
// eslint-disable-next-line rulesdir/no-styled-export
export const AnnouncementMessage = styled.div`
    display: flex;
    flex-direction: column;
    /* Explicit width needed for IE11 */
    width: 100%;
    overflow: hidden;
    /* This will make the chevron expand/shrink icon bust out of the container. */
    flex-shrink: 0;
`;
